// Holiday utility functions for date-based holiday management
// Uses free holiday API (date.nager.at) for accurate, official holiday data

// Import the API utility
import { apiWithPrefix as api } from './api';
import apiCallTracker from './apiCallTracker';

/**
 * Holiday API Configuration
 * Using backend proxy to avoid CORS issues with Calendarific API
 */

/**
 * Default country code for holiday API
 * Fixed to India (IN) as this API key is specifically for Indian holidays
 */
const DEFAULT_COUNTRY_CODE = 'IN'; // India only

/**
 * Cache for API responses to avoid repeated calls
 */
const holidayCache = new Map();

/**
 * Test API connectivity and return status
 * @returns {Promise<Object>} - API status information
 */
export const testHolidayAPI = async () => {
  try {
    // Test backend proxy endpoint
    const response = await api.get('/test-external-api');
    
    return {
      status: 'success',
      message: 'Calendarific API service is accessible via backend proxy',
      details: response.data
    };
  } catch (error) {
    return {
      status: 'error',
      message: 'Failed to connect to backend holiday proxy',
      details: {
        error: error.message,
        response: error.response?.data,
        status: error.response?.status
      }
    };
  }
};

/**
 * Fetch holidays from the backend with caching (optimized for monthly requests)
 * @param {number} year - The year to fetch holidays for
 * @param {number} month - The month to fetch holidays for (1-12)
 * @param {string} countryCode - Country code (fixed to IN for India)
 * @returns {Promise<Array>} - Array of holiday objects
 */
export const fetchHolidaysForMonth = async (year, month, countryCode = DEFAULT_COUNTRY_CODE) => {
  // Force country code to IN (India) since this API key is for India only
  countryCode = 'IN';
  
  const cacheKey = `${year}-${month}-${countryCode}`;
  const endpoint = `/holidays/${year}/${month}/${countryCode}`;
  
  // Check cache first
  if (holidayCache.has(cacheKey)) {
    console.log('✅ Using cached holidays for', cacheKey);
    return holidayCache.get(cacheKey);
  }

  // Check API call limits
  if (!apiCallTracker.shouldAllowCall(endpoint)) {
    console.warn(`🚫 API call blocked for ${endpoint} due to rate limiting`);
    return [];
  }

  try {
    console.log(`🌐 Fetching holidays for ${year}-${month} from cached backend endpoint`);
    
    // Record the API call
    apiCallTracker.recordCall(endpoint);
    
    // Use the new cached endpoint: /holidays/:year/:month/:country
    console.log('Backend API endpoint:', endpoint);
    
    const response = await api.get(endpoint);
    
    console.log('Backend response received:', response.status);
    console.log('Response data:', response.data);
    
    const result = response.data;
    
    if (!result.holidays || !Array.isArray(result.holidays)) {
      console.log('No holidays array in backend response');
      return [];
    }
    
    console.log(`✅ Received ${result.holidays.length} holidays for ${year}-${month} (source: ${result.source})`);
    
    // Cache the result for 1 hour (frontend cache)
    holidayCache.set(cacheKey, result.holidays);
    setTimeout(() => holidayCache.delete(cacheKey), 60 * 60 * 1000);

    return result.holidays;
    
  } catch (error) {
    console.error(`❌ Failed to fetch holidays for ${year}-${month}:`, error);
    console.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    
    return [];
  }
};

/**
 * Fetch holidays from the backend proxy (backward compatibility)
 * @param {number} year - The year to fetch holidays for
 * @param {string} countryCode - Country code (fixed to IN for India)
 * @returns {Promise<Array>} - Array of holiday objects
 */
export const fetchHolidaysFromAPI = async (year, countryCode = DEFAULT_COUNTRY_CODE) => {
  // For backward compatibility, fetch all months for the year
  const allHolidays = [];
  
  for (let month = 1; month <= 12; month++) {
    try {
      const monthHolidays = await fetchHolidaysForMonth(year, month, countryCode);
      allHolidays.push(...monthHolidays);
    } catch (error) {
      console.warn(`Failed to fetch holidays for ${year}-${month}:`, error);
    }
  }
  
  return allHolidays;
};

/**
 * Get available countries from the API
 * @returns {Promise<Array>} - Array of country objects
 */
export const getAvailableCountries = async () => {
  try {
    // OpenHolidays doesn't have a countries endpoint, so return common countries
    return [
      { countryCode: 'IN', name: 'India' },
      { countryCode: 'US', name: 'United States' },
      { countryCode: 'GB', name: 'United Kingdom' },
      { countryCode: 'CA', name: 'Canada' },
      { countryCode: 'AU', name: 'Australia' },
      { countryCode: 'DE', name: 'Germany' },
      { countryCode: 'FR', name: 'France' },
      { countryCode: 'JP', name: 'Japan' },
      { countryCode: 'CN', name: 'China' },
      { countryCode: 'BR', name: 'Brazil' }
    ];
  } catch (error) {
    console.warn('Failed to fetch available countries:', error);
    
    // Return common countries as fallback
    return [
      { countryCode: 'US', name: 'United States' },
      { countryCode: 'IN', name: 'India' },
      { countryCode: 'GB', name: 'United Kingdom' },
      { countryCode: 'CA', name: 'Canada' },
      { countryCode: 'AU', name: 'Australia' },
      { countryCode: 'DE', name: 'Germany' },
      { countryCode: 'FR', name: 'France' }
    ];
  }
};

/**
 * Check if today is a holiday (India only)
 * @param {string} countryCode - Country code (forced to IN)
 * @returns {Promise<boolean>} - True if today is a holiday
 */
export const isTodayHoliday = async (countryCode = DEFAULT_COUNTRY_CODE) => {
  try {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    
    // Get holidays for the current month only
    const holidays = await fetchHolidaysForMonth(year, month, 'IN');
    
    // Check if today matches any holiday
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    return holidays.some(holiday => holiday.date === todayStr);
  } catch (error) {
    console.warn('Failed to check if today is a holiday:', error);
    return false;
  }
};

/**
 * Get next public holidays (India only)
 * @param {string} countryCode - Country code (forced to IN)
 * @returns {Promise<Array>} - Array of upcoming holidays
 */
export const getNextPublicHolidays = async (countryCode = DEFAULT_COUNTRY_CODE) => {
  try {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const nextYear = currentYear + 1;
    
    const allHolidays = [];
    
    // Get holidays for remaining months of current year
    for (let month = currentMonth; month <= 12; month++) {
      try {
        const monthHolidays = await fetchHolidaysForMonth(currentYear, month, 'IN');
        allHolidays.push(...monthHolidays);
      } catch (error) {
        console.warn(`Failed to fetch holidays for ${currentYear}-${month}:`, error);
      }
    }
    
    // Get holidays for first few months of next year
    for (let month = 1; month <= 6; month++) {
      try {
        const monthHolidays = await fetchHolidaysForMonth(nextYear, month, 'IN');
        allHolidays.push(...monthHolidays);
      } catch (error) {
        console.warn(`Failed to fetch holidays for ${nextYear}-${month}:`, error);
      }
    }
    
    // Filter future holidays and sort by date
    const futureHolidays = allHolidays
      .filter(holiday => new Date(holiday.date) > today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 10); // Return next 10 holidays
    
    return futureHolidays;
  } catch (error) {
    console.warn('Failed to fetch next public holidays:', error);
    return [];
  }
};

/**
 * Convert HolidayAPI.com holiday types to our internal type system
 * @param {string} apiType - Holiday type from HolidayAPI.com
 * @returns {string} - Internal holiday type
 */
const getHolidayTypeFromHolidayAPI = (apiType) => {
  if (!apiType) return 'national'; // Default for Indian holidays
  
  const type = apiType.toLowerCase();
  
  if (type.includes('national') || type.includes('public')) return 'national';
  if (type.includes('religious') || type.includes('hindu') || type.includes('muslim') || type.includes('christian') || type.includes('sikh') || type.includes('buddhist')) return 'religious';
  if (type.includes('bank')) return 'bank';
  if (type.includes('school')) return 'school';
  if (type.includes('government') || type.includes('federal') || type.includes('state')) return 'government';
  if (type.includes('optional') || type.includes('observance') || type.includes('restricted')) return 'optional';
  if (type.includes('local') || type.includes('regional')) return 'observance';
  
  return 'national'; // Default for Indian holidays
};

/**
 * Convert AbstractAPI holiday types to our internal type system (legacy)
 * @param {string} apiType - Holiday type from AbstractAPI
 * @returns {string} - Internal holiday type
 */
const getHolidayTypeFromAbstractAPI = (apiType) => {
  if (!apiType) return 'public';
  
  const type = apiType.toLowerCase();
  
  if (type.includes('national') || type.includes('public')) return 'national';
  if (type.includes('religious')) return 'religious';
  if (type.includes('bank')) return 'bank';
  if (type.includes('school')) return 'school';
  if (type.includes('government') || type.includes('federal')) return 'government';
  if (type.includes('optional') || type.includes('observance')) return 'optional';
  if (type.includes('local')) return 'observance';
  
  return 'public';
};

/**
 * Convert OpenHolidays API holiday types to our internal type system (legacy)
 * @param {string} apiType - Holiday type from OpenHolidays API
 * @returns {string} - Internal holiday type
 */
const getHolidayTypeFromOpenHolidays = (apiType) => {
  if (!apiType) return 'public';
  
  const type = apiType.toLowerCase();
  
  if (type.includes('public') || type.includes('national')) return 'national';
  if (type.includes('bank')) return 'bank';
  if (type.includes('school')) return 'school';
  if (type.includes('religious')) return 'religious';
  if (type.includes('government') || type.includes('authorities')) return 'government';
  if (type.includes('optional')) return 'optional';
  if (type.includes('observance')) return 'observance';
  
  return 'public';
};

/**
 * Convert API holiday types to our internal type system (legacy function for compatibility)
 * @param {Array} apiTypes - Array of types from the API
 * @returns {string} - Internal holiday type
 */
const getHolidayTypeFromAPI = (apiTypes) => {
  if (!apiTypes || !Array.isArray(apiTypes)) return 'public';
  
  if (apiTypes.includes('Public')) return 'national';
  if (apiTypes.includes('Bank')) return 'bank';
  if (apiTypes.includes('School')) return 'school';
  if (apiTypes.includes('Authorities')) return 'government';
  if (apiTypes.includes('Optional')) return 'optional';
  if (apiTypes.includes('Observance')) return 'observance';
  
  return 'public';
};

/**
 * Get upcoming holidays (API only) within a date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} countryCode - Country code for API holidays
 * @returns {Promise<Array>} - Array of upcoming holiday objects
 */
export const getUpcomingHolidays = async (startDate, endDate, countryCode = DEFAULT_COUNTRY_CODE) => {
  console.log('getUpcomingHolidays called with:', {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
    countryCode
  });
  
  const allHolidays = [];
  
  // Get all months in the date range
  const startYear = startDate.getFullYear();
  const startMonth = startDate.getMonth() + 1;
  const endYear = endDate.getFullYear();
  const endMonth = endDate.getMonth() + 1;
  
  // Fetch holidays month by month to optimize API usage
  for (let year = startYear; year <= endYear; year++) {
    const monthStart = year === startYear ? startMonth : 1;
    const monthEnd = year === endYear ? endMonth : 12;
    
    for (let month = monthStart; month <= monthEnd; month++) {
      try {
        console.log(`Fetching holidays for ${year}-${month} in getUpcomingHolidays...`);
        const monthHolidays = await fetchHolidaysForMonth(year, month, countryCode);
        console.log(`Received ${monthHolidays.length} holidays for ${year}-${month}`);
        allHolidays.push(...monthHolidays);
      } catch (error) {
        console.warn(`Failed to fetch holidays for ${year}-${month}:`, error);
      }
    }
  }
  
  console.log(`Total holidays fetched: ${allHolidays.length}`);
  
  // Filter holidays within the date range
  const filteredHolidays = allHolidays
    .filter(holiday => {
      const holidayDate = new Date(holiday.date);
      const inRange = holidayDate >= startDate && holidayDate <= endDate;
      if (inRange) {
        console.log(`Holiday in range: ${holiday.name} on ${holiday.date}`);
      }
      return inRange;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
    
  console.log(`Filtered upcoming holidays: ${filteredHolidays.length}`);
  return filteredHolidays;
};

/**
 * Format date for API calls (DD/MM/YYYY)
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDateForAPI = (date) => {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Parse different date formats and return a standardized Date object
 * @param {string} dateString - Date string in various formats
 * @returns {Date|null} - Parsed date or null if invalid
 */
export const parseHolidayDate = (dateString) => {
  if (!dateString) return null;
  
  // Try different date formats
  const formats = [
    // ISO format: YYYY-MM-DD
    /^\d{4}-\d{2}-\d{2}$/,
    // DD/MM/YYYY format
    /^\d{2}\/\d{2}\/\d{4}$/,
    // MM/DD/YYYY format
    /^\d{2}\/\d{2}\/\d{4}$/
  ];
  
  try {
    // Try ISO format first
    if (formats[0].test(dateString)) {
      return new Date(dateString);
    }
    
    // Try DD/MM/YYYY format
    if (formats[1].test(dateString)) {
      const [day, month, year] = dateString.split('/');
      return new Date(year, month - 1, day);
    }
    
    // Fallback to native Date parsing
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.warn('Failed to parse date:', dateString, error);
    return null;
  }
};

/**
 * Check if two dates represent the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} - True if same day
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  return date1.toDateString() === date2.toDateString();
};

/**
 * Get holiday type color class
 * @param {string} type - Holiday type
 * @returns {string} - CSS class for styling
 */
export const getHolidayTypeColor = (type) => {
  switch (type) {
    case 'national':
    case 'public':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'international':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'religious':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    case 'company':
    case 'bank':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'school':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    case 'government':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'optional':
    case 'observance':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    default:
      return 'bg-purple-100 text-purple-700 border-purple-200';
  }
};

/**
 * Calculate days until a holiday
 * @param {string|Date} holidayDate - Holiday date
 * @returns {string} - Human readable time until holiday
 */
export const getDaysUntilHoliday = (holidayDate) => {
  const today = new Date();
  const holiday = typeof holidayDate === 'string' ? new Date(holidayDate) : holidayDate;
  
  // Set time to start of day for accurate comparison
  today.setHours(0, 0, 0, 0);
  holiday.setHours(0, 0, 0, 0);
  
  const diffTime = holiday - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
  return `${diffDays} days`;
};

/**
 * Merge API holidays with internal database holidays, removing duplicates
 * @param {Array} apiHolidays - Holidays from external API
 * @param {Array} internalHolidays - Holidays from internal database
 * @returns {Array} - Merged and deduplicated holidays
 */
export const mergeHolidays = (apiHolidays, internalHolidays) => {
  const merged = [...apiHolidays];
  
  internalHolidays.forEach(internal => {
    // Check if this internal holiday already exists in API holidays
    const exists = apiHolidays.some(api => {
      const apiDate = parseHolidayDate(api.date);
      const internalDate = parseHolidayDate(internal.date);
      return apiDate && internalDate && isSameDay(apiDate, internalDate);
    });
    
    if (!exists) {
      merged.push({
        ...internal,
        source: 'internal'
      });
    }
  });
  
  return merged.sort((a, b) => {
    const dateA = parseHolidayDate(a.date);
    const dateB = parseHolidayDate(b.date);
    return dateA - dateB;
  });
};

/**
 * Clear holiday cache (useful for testing or manual refresh)
 */
export const clearHolidayCache = () => {
  holidayCache.clear();
};

/**
 * Get cache statistics
 * @returns {Object} - Cache statistics
 */
export const getCacheStats = () => {
  return {
    size: holidayCache.size,
    keys: Array.from(holidayCache.keys())
  };
};
/**
 * Get API call statistics for monitoring usage
 * @returns {Object} - API call statistics
 */
export const getAPICallStats = () => {
  return apiCallTracker.getStats();
};

/**
 * Reset API call tracking (useful for testing)
 */
export const resetAPICallTracking = () => {
  apiCallTracker.reset();
};

/**
 * Log current API usage statistics
 */
export const logAPIUsage = () => {
  const stats = getAPICallStats();
  console.log('📊 Holiday API Usage Statistics:');
  console.table(stats);
  
  const totalCalls = Object.values(stats).reduce((sum, stat) => sum + stat.totalCalls, 0);
  const recentCalls = Object.values(stats).reduce((sum, stat) => sum + stat.recentCalls, 0);
  
  console.log(`Total API calls made: ${totalCalls}`);
  console.log(`Recent API calls (last minute): ${recentCalls}`);
  
  if (totalCalls > 10) {
    console.warn('⚠️ High API usage detected. Consider checking for unnecessary calls.');
  }
  
  return { totalCalls, recentCalls, endpoints: stats };
};